import {ChangeDetectionStrategy, Component, effect, Injectable, input, output} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from "@angular/cdk/drag-drop";
import {GruppeId, SakstypeId, SakstypeModell, SakstypeRessursClient, SakstypeSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatIcon} from "@angular/material/icon";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {håndterRedigerListeEndringer} from "felles-ng2/infrastruktur/angular/redigerListeSkjema";

export type SakstypeSkjema = ReturnType<typeof GruppeSakstypeRedigerListeKomponent.opprettSkjemarad>
export type SakstypeSkjemaVerdier = SakstypeSkjema['value']

@Component({
    selector: 'gruppe-sakstype-rediger-liste',
    templateUrl: './gruppe-sakstype-rediger-liste.html',
    styleUrls: ['./gruppe-sakstype-rediger-liste.scss'],
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslateModule,
        StandardValideringsfeilPipe,
        MatIcon,
        KortHandlingerKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeSakstypeRedigerListeKomponent {

    /** Kalles når bruker har lagret eller avbryter, returnerer oppdaterte sakstyper ved lagret, null ved avbrutt */
    vedFerdig = output<SakstypeModell[] | null>();

    sakstyper = input.required<SakstypeModell[]>();
    gruppeId = input.required<GruppeId>();

    lagrer = asynkArbeidPågårSignal();

    skjema = new FormArray<SakstypeSkjema>([]);

    static opprettSkjemarad(verdier = {id: null as SakstypeId | null, tittel: '', ny: true, kanIkkeSlettes: false}) {
        return new FormGroup({
            id: new FormControl<SakstypeId | null>(verdier.id),
            tittel: new FormControl<string>(verdier.tittel, {nonNullable: true, validators: FeltValidatorer.litenStreng}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            kanIkkeSlettes: new FormControl<boolean>(verdier.kanIkkeSlettes, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
    }

    constructor(private avhengigheter: GruppeSakstypeRedigerListeKomponentAvhengigheter) {
        this.skjema.valueChanges.pipe(takeUntilDestroyed())
          .subscribe(() => håndterRedigerListeEndringer(this.skjema, GruppeSakstypeRedigerListeKomponent.opprettSkjemarad));

        effect(() => {
            this.skjema.clear();
            this.sakstyper().forEach(sakstype =>
              this.skjema.push(GruppeSakstypeRedigerListeKomponent.opprettSkjemarad({...sakstype, tittel: sakstype.navn, ny: false}), {emitEvent: false}));
            //sørger for at det blir lagt til en tom 'nytt innslag' rad
            håndterRedigerListeEndringer(this.skjema, GruppeSakstypeRedigerListeKomponent.opprettSkjemarad);
        });
    }

    async lagre() {
        const oppdaterteSakstyper = await this.lagrer.utfør(() =>
          this.avhengigheter.oppdaterSakstyper(this.gruppeId(), this.somSpesifikasjon()));
        this.vedFerdig.emit(oppdaterteSakstyper);
    }

    avbryt() {
        this.vedFerdig.emit(null);
    }

    slett(indeks: number) {
        const rad = this.skjema.at(indeks);
        if (rad.value.kanIkkeSlettes) return;

        if (rad.value.ny) {
            this.skjema.removeAt(indeks);
        } else {
            rad.disable();
            rad.controls.slettet.setValue(true);
        }
    }

    angreSlett(indeks: number) {
        const rad = this.skjema.at(indeks);
        rad.enable();
        rad.controls.slettet.setValue(false);
    }

    /**
     * Predicate function that only allows even numbers to be
     * sorted into even indices and odd numbers at odd indices.
     */
    flyttingTillatFn() {
        return (indeks: number, flyttetRad: CdkDrag<number>) => {
            //this.erFlyttingTillat(indeks, flyttetRad.data);
            console.log('# sorteringspredikat', indeks, flyttetRad);
            return indeks < this.skjema.controls.length - 1;
        }

    }

    radFlyttet(event: CdkDragDrop<any>) {
        const rad = this.skjema.at(event.previousIndex);
        this.skjema.removeAt(event.previousIndex);
        this.skjema.insert(event.currentIndex, rad);
    }

    private somSpesifikasjon(): SakstypeSpesifikasjon[] {
        return this.skjema.controls
          .filter(rad => !rad.value.slettet && !erTomEllerNil(rad.value.tittel))
          .map(rad => ({
              id: rad.value.id as unknown as SakstypeId,
              navn: rad.value.tittel!
          }));
    }
}

@Injectable()
export class GruppeSakstypeRedigerListeKomponentAvhengigheter {

    constructor(private sakstypeRessurs: SakstypeRessursClient) {
    }

    oppdaterSakstyper(gruppeId: GruppeId, sakstyper: SakstypeSpesifikasjon[]): Promise<SakstypeModell[]> {
        return this.sakstypeRessurs.oppdater({gruppeId, sakstyper}).somPromise();
    }
}
